import { Dialog } from 'components/Dialog';
import { usePhrases } from 'contexts/ConfigContext';
import { useLoyaltyRewards } from 'contexts/LoyaltyRewardContext';
import { useGiftCard } from 'contexts/GiftCardContext';
import { useVouchers } from 'contexts/VoucherContext';
import { useCheckout } from 'contexts/CheckoutContext';

interface ConfirmLeaveCheckoutModalProps {
  show: boolean;
  proceed: () => void;
  dismiss: () => void;
  logOutOnDismiss?: boolean;
  logOut?: () => void;
  skipRemoveRedeemables?: boolean;
}

export const ConfirmLeaveCheckoutModal: React.FC<
  ConfirmLeaveCheckoutModalProps
> = ({
  show,
  proceed,
  dismiss,
  logOutOnDismiss,
  logOut,
  skipRemoveRedeemables,
}) => {
  const {
    giftCard: { giftCardPhrase },
    loyalty: { rewardPhrase },
    vouchering: { voucherPhrase },
  } = usePhrases();
  const { redeemedLoyaltyRewards } = useLoyaltyRewards();
  const { redeemedGiftCards } = useGiftCard();
  const { redeemedVouchers } = useVouchers();
  const { removeAllRedeemables } = useCheckout();

  const hasGiftCards = redeemedGiftCards.length > 0;
  const hasLoyaltyRewards = redeemedLoyaltyRewards.length > 0;
  const hasRedeemedVouchers = redeemedVouchers.length > 0;

  const hasRedeemables =
    hasGiftCards || hasLoyaltyRewards || hasRedeemedVouchers;

  const phraseToUse = () => {
    if (hasGiftCards) {
      return giftCardPhrase;
    } else if (hasLoyaltyRewards) {
      return rewardPhrase;
    } else if (hasRedeemedVouchers) {
      return voucherPhrase;
    }
  };

  const handleDismissAndLogout = () => {
    if (!skipRemoveRedeemables) {
      removeAllRedeemables();
    }
    dismiss();
    if (logOutOnDismiss && logOut) {
      // The log out function is handled outside the component
      // due to errors when signing the user out from here
      logOut();
    }
  };

  const handleDismissAndClose = () => {
    if (!skipRemoveRedeemables) {
      removeAllRedeemables();
    }
    proceed();
  };

  return hasRedeemables ? (
    <Dialog
      isOpen={show}
      title={`${phraseToUse()} in Basket`}
      description={`It looks like you have a ${phraseToUse()} in your basket. All ${phraseToUse()}s will be removed from your basket and will need to be re-added. Do you wish to continue?`}
      accept={{
        action: () =>
          logOutOnDismiss ? handleDismissAndLogout() : handleDismissAndClose(),
        text: 'Remove and continue',
        btnType: 'destructive',
      }}
      reject={{ action: dismiss, text: 'Cancel' }}
    />
  ) : null;
};
